import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

// Horizon's legacy cart event uses one event name for adds and ordinary cart
// updates. These are the add-only sources currently emitted by this theme.
const CART_ADD_SOURCES = new Set([
  'product-form-component',
  'gf-product-card',
  'gfx-band',
  'gf-bundle',
  'gf-pdp-bundle',
]);

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {boolean} */
  #openPending = false;

  /** @type {HTMLElement | null} */
  #focusReturnTarget = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#restoreFocus);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    const data = event.detail?.data;
    const isSuccessfulAdd = !data?.didError && CART_ADD_SOURCES.has(data?.source);

    if (this.hasAttribute('auto-open') && isSuccessfulAdd) {
      // Quick-add owns a modal dialog too. Let it finish closing and restore
      // focus before the cart captures focus.
      const sourceDialog =
        event.target instanceof Element ? /** @type {HTMLDialogElement | null} */ (event.target.closest('dialog')) : null;

      if (sourceDialog?.open && sourceDialog !== this.refs.dialog) {
        sourceDialog.addEventListener('close', () => this.open(), { once: true });
      } else {
        const sourceElement = event.target instanceof Element ? event.target : null;
        const sourceSection = sourceElement?.closest('.shopify-section');
        const sourceProductId =
          sourceElement?.matches('product-form-component') && sourceElement instanceof HTMLElement
            ? sourceElement.dataset.productId
            : null;
        const pendingStickyButton = sourceProductId
          ? Array.from(
              sourceSection?.querySelectorAll(
                'sticky-add-to-cart [ref="addToCartButton"][data-cart-focus-return="true"]'
              ) ?? []
            ).find(
              (button) =>
                button.closest('sticky-add-to-cart') instanceof HTMLElement &&
                button.closest('sticky-add-to-cart')?.dataset.productId === sourceProductId
            )
          : null;
        const nativeButton =
          sourceElement?.matches('product-form-component')
            ? sourceElement.querySelector('[ref="addToCartButton"]')
            : null;
        const focusTarget =
          pendingStickyButton instanceof HTMLElement
            ? pendingStickyButton
            : nativeButton instanceof HTMLElement
              ? nativeButton
              : null;

        this.open(focusTarget);
      }
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /** @param {HTMLElement | Event | null} [focusTarget] */
  open(focusTarget) {
    if (focusTarget instanceof HTMLElement) {
      this.#focusReturnTarget = focusTarget;
    } else if (!this.#focusReturnTarget && document.activeElement instanceof HTMLElement) {
      this.#focusReturnTarget = document.activeElement;
    }

    // `showDialog()` opens on the next animation frame. Guard that pending
    // frame as well as `dialog.open` so two consumers of the same cart event
    // cannot schedule two showModal() calls.
    if (this.refs.dialog?.open || this.#openPending) return;

    this.#openPending = true;
    this.showDialog();
    requestAnimationFrame(() => {
      this.#openPending = false;
    });

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #restoreFocus = () => {
    const focusTarget = this.#focusReturnTarget;
    this.#focusReturnTarget = null;

    if (
      focusTarget?.isConnected &&
      !(focusTarget instanceof HTMLButtonElement && focusTarget.disabled) &&
      !focusTarget.hasAttribute('disabled')
    ) {
      focusTarget.focus({ preventScroll: true });
      if (document.activeElement === focusTarget) return;
    }

    const drawerTrigger = this.querySelector('[data-testid="cart-drawer-trigger"]');
    if (drawerTrigger instanceof HTMLElement && !drawerTrigger.hasAttribute('disabled')) {
      drawerTrigger.focus({ preventScroll: true });
    }
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
